/* eslint-disable css-modules/no-unused-class  */
import { useEffect, useState, FC } from 'react';
import { useSelector } from 'react-redux';
import { getPlatformUrl } from 'utils';
import { FullPageLoader } from 'learning-panel-components';
import { selectIsLeanUser, selectRole, selectUserUuid } from '@redux/slices/userSlice';
import { useGetPrimaryCourseData } from '@queries/platform/userContent';
import PrimaryCourse from './primaryCourse';

const PrimaryCourseContainer: FC<{ onChangePrimaryCourse: Function; setIsPrimaryCourseDialogOpen: Function }> = ({
    onChangePrimaryCourse,
    setIsPrimaryCourseDialogOpen
}: {
    onChangePrimaryCourse: Function;
    setIsPrimaryCourseDialogOpen: Function;
}) => {
    const userUuid = useSelector(selectUserUuid);
    const isLeanUser = useSelector(selectIsLeanUser);
    const role = useSelector(selectRole);
    const isAdmin = role === 'admin' || role === 'superAdmin';
    const [primaryCourse, setPrimaryCourse] = useState<any>(null);
    const [hasNoPrimaryCourse, setHasNoPrimaryCourse] = useState<boolean>(false);

    const { data: primaryCourseData, isLoading, isError } = useGetPrimaryCourseData(userUuid);

    useEffect(() => {
        if (!primaryCourseData?.data) return;

        if (Object.keys(primaryCourseData.data).length === 0) {
            setHasNoPrimaryCourse(true);
            // Admins/super admins have no primary course by design: don't force the
            // blocking "select primary course" dialog on them. Regular users still get it.
            if (!isAdmin) setIsPrimaryCourseDialogOpen(true);
            return;
        }

        setHasNoPrimaryCourse(false);
        const { course, lessons } = primaryCourseData.data;
        const primaryCourseTmp = {
            course: {
                uuid: course.uuid,
                displayName: course.display_name,
                completionPercentage: course.completion_percentage,
                score: course.score,
                iconUrl: course.icon_url ?? `${process.env.PUBLIC_URL}/images/courseDefaultCard.svg`,
                totalLessons: course.total_lessons,
                completedLessons: course.completed_lessons
            },
            lessons: lessons.map((lesson: any) => {
                return {
                    uuid: lesson.uuid,
                    lessonDisplayName: lesson.display_name,
                    lessonStatus: lesson.status,
                    lessonUrl: lesson.url,
                    activeOnLean: lesson.active_on_lean
                };
            })
        };
        setPrimaryCourse(primaryCourseTmp);
    }, [primaryCourseData]);

    if (isLoading || (primaryCourse === null && !hasNoPrimaryCourse && !isError)) {
        return <FullPageLoader />;
    }

    if (hasNoPrimaryCourse || isError) {
        // No primary course, or the metadata request failed: render nothing instead of
        // spinning the loader forever. Regular users with no course are prompted by the
        // parent's selection dialog; admins are not.
        return null;
    }

    return (
        <PrimaryCourse
            courseName={primaryCourse?.course?.displayName}
            courseUrl={`${getPlatformUrl()}/app/course?courseUuid=${primaryCourse?.course?.uuid}`}
            courseLessons={primaryCourse?.lessons || []}
            isLoading={isLoading}
            onChangePrimaryCourse={() => onChangePrimaryCourse(true)}
            score={primaryCourse?.course?.score}
            iconUrl={primaryCourse?.course?.iconUrl}
            completionPercentage={primaryCourse?.course?.completionPercentage}
            totalLessons={primaryCourse?.course?.totalLessons}
            competedLessons={primaryCourse?.course?.completedLessons}
            isLeanUser={isLeanUser}
        ></PrimaryCourse>
    );
};

export default PrimaryCourseContainer;
